import { useEffect, useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import { ErrorNotice, Loading } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { get, post, remove } from '../lib/api';

type Session = {
  id: string;
  device_label: string;
  anomaly_level: string;
  last_seen_at: string;
  current: number;
};

export function AccountPage({ navigate }: { navigate: (path: string) => void }) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    try {
      const result = await get<{ sessions: Session[] }>('/api/account/sessions');
      setSessions(result.sessions);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sesi tidak dapat dimuat.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function accept(consentType: 'terms' | 'privacy' | 'ai_processing') {
    await post('/api/account/consents', { consentType, documentVersion: 'v1.0', granted: true });
  }

  async function requestDeletion() {
    const confirmation = window.prompt('Ketik HAPUS PERMANEN untuk mengajukan penghapusan akun.');
    if (!confirmation) return;
    try {
      await post('/api/account/data-requests', {
        requestType: 'account_delete',
        confirmation
      });
      window.alert('Permintaan penghapusan tercatat.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Permintaan belum tercatat.');
    }
  }

  return (
    <div className="account-page">
      <header className="dashboard-header">
        <button className="brand-button" onClick={() => navigate('/app')}>
          <BrandMark />
        </button>
        <button className="button button-secondary" onClick={() => navigate('/app')}>
          Kembali
        </button>
      </header>
      <main className="account-main">
        <div className="page-heading">
          <div>
            <span className="eyebrow">Keamanan dan privasi</span>
            <h1>Akun Anda</h1>
            <p>Kelola sesi aktif, persetujuan, ekspor data, dan permintaan penghapusan.</p>
          </div>
          <a className="button button-primary" href="/api/account/export" download>
            <SenoIcon name="download" /> Ekspor data akun
          </a>
        </div>
        {error ? <ErrorNotice message={error} /> : null}
        <section className="account-section">
          <div className="section-title">
            <div>
              <span className="eyebrow">Perangkat</span>
              <h2>Sesi aktif</h2>
            </div>
          </div>
          {loading ? (
            <Loading label="Memeriksa sesi…" />
          ) : (
            <div className="session-list">
              {sessions.map((session) => (
                <article key={session.id}>
                  <SenoIcon name="security" />
                  <div>
                    <strong>{session.device_label || 'Perangkat tidak dikenal'}</strong>
                    <span>
                      Terakhir aktif {new Date(session.last_seen_at).toLocaleString('id-ID')}
                      {session.anomaly_level === 'review' ? ' · perlu ditinjau' : ''}
                    </span>
                  </div>
                  {session.current ? (
                    <span className="badge badge-active">Sesi ini</span>
                  ) : (
                    <button
                      className="button button-secondary"
                      onClick={async () => {
                        await remove(`/api/account/sessions/${session.id}`);
                        await load();
                      }}
                    >
                      Cabut sesi
                    </button>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
        <section className="account-section consent-section">
          <div>
            <h2>Kebijakan dan persetujuan</h2>
            <p>
              Dokumen akademik tetap tanggung jawab pengguna. AI membantu menyusun, bukan
              menggantikan pedoman kampus.
            </p>
          </div>
          <div className="row-actions">
            <button className="button button-secondary" onClick={() => accept('terms')}>
              Setujui Ketentuan
            </button>
            <button className="button button-secondary" onClick={() => accept('privacy')}>
              Setujui Privasi
            </button>
            <button className="button button-secondary" onClick={() => accept('ai_processing')}>
              Setujui pemrosesan AI
            </button>
          </div>
          <nav className="legal-links" aria-label="Dokumen kebijakan">
            <button onClick={() => navigate('/privacy')}>Kebijakan Privasi</button>
            <button onClick={() => navigate('/terms')}>Ketentuan Layanan</button>
            <button onClick={() => navigate('/ai-disclosure')}>Pengungkapan AI</button>
            <button onClick={() => navigate('/refund-policy')}>Kebijakan Refund</button>
          </nav>
        </section>
        <section className="account-section danger-section">
          <h2>Penghapusan akun</h2>
          <p>
            Arsip, trash, dan penghapusan permanen adalah status berbeda. Permintaan permanen
            diproses sesuai masa retensi dan backup purge.
          </p>
          <button className="button button-danger" onClick={requestDeletion}>
            Ajukan penghapusan akun
          </button>
        </section>
      </main>
    </div>
  );
}
